/**
 * /ko/product-roadmap — 제품 로드맵 concept page (Korean).
 * Links /ko/product-discovery + /ko/decision-log.
 */
import { MarketingShell } from './MarketingShell';

export function KoProductRoadmap() {
  return (
    <MarketingShell
      currentLocale="ko"
      hreflangPath="/product-roadmap"
      hreflangLocales={['ko']}
      documentTitle="제품 로드맵 작성법｜양식, 예시, 실전 팁 · Draftlize"
      documentDescription="제품(프로덕트) 로드맵 작성법을 형식·예시와 함께 정리하고, 범위가 바뀌어도 낡지 않는 '살아있는 로드맵' 만드는 법을 안내합니다."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">가이드 · 로드맵</span>
          <h1 className="serif" style={{ fontSize: 'clamp(31px, 4.2vw, 54px)', lineHeight: 1.1, margin: '12px 0 24px' }}>
            범위 변경에도 살아남는<br />
            <em>제품 로드맵.</em>
          </h1>
          <p className="lede">
            제품 로드맵은 제품이 <em>어디로</em> 가는지, 그리고 <em>왜</em>인지를 보여 줍니다. 세부가 아니라 방향과 순서죠. 어려운 건 만드는 게 아니라, 로드맵이 '고정된 슬라이드'로 끝난다는 것입니다——3주 뒤 결정이 바뀌어도 로드맵은 그대로라, 아무도 믿지 않게 됩니다. 이 페이지는 형식과 예시, 그리고 로드맵을 결정에 연결해 '계속 참'으로 유지하는 법을 다룹니다.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">실제로 통하는 로드맵 형식.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Now / Next / Later</strong>——거짓 날짜 정밀도를 피할 수 있어 초기에 최적.</li>
            <li><strong>테마·성과 기반</strong>——기능이 아니라 목표로 배열.</li>
            <li><strong>시간 기반(분기)</strong>——의존성으로 기한이 굳을 때.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            <strong>미니 예시(Now/Next/Later):</strong> Now — 온보딩 마찰 줄이기 · Next — 셀프서브 결제 · Later — 팀 워크스페이스. 각 항목엔 <em>왜</em>——그것을 로드맵에 올린 결정——가 붙습니다. 슬라이드 버전이 잃는 게 바로 이 '왜'입니다.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">고정 vs 살아있음</span>
            <h2 className="serif">슬라이드 로드맵 <em>vs 결정과 함께 움직이는 로드맵.</em></h2>
          </div>
          <p>로드맵은 일련의 결정에서 '보이는 끝'일 뿐입니다. 둘을 떼어 놓으면 조용히 낡습니다.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">연동된 로드맵(Draftlize)</th>
              <th>슬라이드 로드맵</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>항목별 '왜'</td><td className="ours yes">결정 카드에 연결</td><td className="no">머릿속·흩어짐</td></tr>
            <tr><td>결정이 바뀔 때</td><td className="ours yes">해당 항목이 stale</td><td className="no">로드맵은 옛날 그대로</td></tr>
            <tr><td>3주 후</td><td className="ours yes">최신 여부 한눈에</td><td className="no">아무도 안 믿음</td></tr>
            <tr><td>AI 에이전트가 사용</td><td className="ours yes">MCP로 맥락 읽음</td><td className="partial">그냥 평면 슬라이드</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          대부분의 로드맵은 계획이 틀린 게 아니다. 마지막 결정 이후 3주 동안 아무도 따라 업데이트하지 않았을 뿐이다.
        </blockquote>
        <cite>로드맵을 결정에 연결하면, 결정이 바뀔 때 자동으로 따라온다. 그게 Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">자주 묻는 질문</span>
            <h2 className="serif">FAQ.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '로드맵과 백로그의 차이는?', a: '로드맵은 성과 단위로 방향과 순서를 보여 주고, 백로그는 구체적 작업의 우선순위 목록입니다. 로드맵은 "왜/어디로", 백로그는 "다음에 무엇을"에 답합니다.' },
            { q: '기간은 얼마나?', a: '정직하게 방향을 줄 수 있는 범위까지. 고정 분기보다 Now/Next/Later가 더 효과적일 때가 많습니다. 거짓 날짜 정밀도가 로드맵 신뢰를 무너뜨리는 주된 원인입니다.' },
            { q: '범위 변경은 어떻게 관리하나요?', a: '항목 뒤의 결정이 바뀌면 항목도 바뀌어야 합니다. Draftlize에서는 둘이 연결되어, 바뀐 결정이 관련 로드맵 부분을 자동으로 stale 처리합니다.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.85, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
        <p style={{ maxWidth: 820, margin: '8px auto 0', fontSize: 15, color: 'var(--ink-1)' }}>
          관련: <a href="/ko/product-discovery" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>프로덕트 디스커버리</a> · <a href="/ko/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>의사결정 기록</a>
        </p>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">자동으로 따라오는 로드맵 <em>—— $5 무료로.</em></h2>
        </div>
        <div className="trust-strip">
          <span>신규 가입 시 $5 지급</span>
          <span>사용한 만큼만 결제</span>
          <span>잔액은 만료되지 않음</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            로드맵을 슬라이드가 아니라 연결된 카드로. 결정이 바뀌면 로드맵이 자동으로 따라옵니다——3주 뒤가 아니라.
          </p>
          <a className="btn-primary" href="/">$5 무료로 시작하기</a>
        </div>
      </section>
    </MarketingShell>
  );
}
